import React, { useEffect, useState } from "react";
import MuiAppBar from "@mui/material/AppBar";
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import { Grid } from "@mui/material";
import TopRightMenu from "./TopRightMenu";
import Hidden from '@mui/material/Hidden';
import QNavigationWrapper from "../../components/Navigation/QNavigationWrapper";
import { QFlowLogoSVG } from "../../components/img/QFlowLogoSVG";
import { Link } from "react-router-dom";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ButtonBase from "@mui/material/ButtonBase";
import { useAppContext } from "../../libs/appContextLib";
import MobileMarketingMenu from "../MarketingPages/MobileMarketingMenu";
import ProductMarketingMenuv3 from "../MarketingPages/ProductMarketingMenuv3";
import { styled, useTheme } from '@mui/material/styles';
import { ButtonGroup } from '@mui/material';
import ScrollToStyleAppBar from "./ScrollToStyleAppBar";
import { get } from "lodash";
import { Box } from "@mui/material";
import { colors, radius } from "../RecipeDesigner/styles/designTokens";

const marketingNavButtonSx = {
    textTransform: "none",
    fontSize: "0.9375rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
    color: colors.text.primary,
    px: 1.25,
    py: 0.5,
    borderRadius: radius.md,
    transition: "background-color 120ms ease",
    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
};


const openedMixin = (theme, drawerWidth, isAuthenticated) => ({
    width: drawerWidth,
    whiteSpace: "nowrap",
    overflowX: 'hidden',
    [theme.breakpoints.up('md')]: {
        width: drawerWidth,
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        background: "#FFF",
        color: theme.palette.primary.main,
        whiteSpace: "nowrap",
    }, [theme.breakpoints.down('md')]: {
        width: isAuthenticated ? 240 : "100%",
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        background: "#FFF",
        color: theme.palette.primary.main,
        whiteSpace: "nowrap",
    },
    color: theme.palette.primary.main,
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('md')]: {
        transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: "hidden",
        whiteSpace: "nowrap",
        width: theme.spacing(7) + 1,
        [theme.breakpoints.up("xs")]: {
            width: theme.spacing(9) + 1,
        },
        background: "#FFF",
        color: theme.palette.primary.main,
    },
    color: theme.palette.primary.main,
    [theme.breakpoints.down('md')]: {
        display: "none",
        overflowX: "hidden",
    },
});

const DrawerHeader = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isAuthenticated',
})(({ theme, isAuthenticated }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minHeight: isAuthenticated ? 42 : 56,
    maxHeight: isAuthenticated ? 42 : 56,
    [theme.breakpoints.up('sm')]: {
        minHeight: isAuthenticated ? 42 : 64,
        maxHeight: isAuthenticated ? 42 : 64,
    },
}));

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) =>
        !['open', 'top', 'isAuthenticated', 'showQPilotChat', 'isMobile', 'drawerWidth'].includes(prop),
})(({ theme, open, top, isAuthenticated, showQPilotChat, isMobile, drawerWidth }) => {
    const collapsed = theme.spacing(7) + 1;
    return {
        zIndex: (isMobile && showQPilotChat) ? 1400 : theme.zIndex.drawer,
        transition: theme.transitions.create(['width', 'margin', 'top'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        top: top || 0,
        position: 'fixed',
        width: isAuthenticated
            ? `calc(100% - ${open ? drawerWidth : collapsed}px)`
            : '100%',
        marginLeft: isAuthenticated && open ? collapsed : 0,
    };
});

const AuthenticatedDrawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) =>
        !['open', 'showQPilotChat', 'isMobile', 'drawerWidth', 'isAuthenticated'].includes(prop),
})(({ theme, open, showQPilotChat, isMobile, drawerWidth, isAuthenticated }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme, drawerWidth, isAuthenticated),
        '& .MuiDrawer-paper': {
            ...openedMixin(theme, drawerWidth, isAuthenticated),
            backgroundColor: '#FFF !important',
            zIndex: (isMobile && showQPilotChat && open) ? 1350 : (isMobile && open) ? 1250 : theme.zIndex.drawer,
        },
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': {
            ...closedMixin(theme),
            backgroundColor: '#FFF !important',
        },
    }),
}));

const UnauthenticatedDrawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) =>
        !['open', 'drawerWidth', 'isAuthenticated'].includes(prop),
})(({ theme, open, drawerWidth, isAuthenticated }) => ({
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    color: "white",
    [theme.breakpoints.down('md')]: {
        width: 240,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    [theme.breakpoints.up('md')]: {
        width: 240,
        flexShrink: 0,
        whiteSpace: 'nowrap',
    },
    ...(open && {
        ...openedMixin(theme, drawerWidth, isAuthenticated),
        '& .MuiDrawer-paper': openedMixin(theme, drawerWidth, isAuthenticated),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export default function QNavBar(props) {

    const theme = useTheme();


    const {
        currUser,
        isAuthenticated,
        activeAccount,
        availableAccounts = [],
        handleAccountSwitch,
    } = useAppContext();


    // No preventDefault here: navigating rows in the mobile menu rely on the
    // anchor's native navigation proceeding while the drawer closes.
    const handleMarketingMenuClose = () => {
        props.handleMobileMarketingDrawerToggle()
    };


    const localStateChatWidgetVisible = localStorage.getItem("chatWidgetVisibleLocal")

    const [chatWidgetVisible, setChatWidgetVisible] = React.useState(localStateChatWidgetVisible === 'true')


    const { subscriptionStatus } = useAppContext();
    const stripeCustomerId = get(subscriptionStatus, 'stripe_customer_id', undefined)

    useEffect(() => {
        setChatWidgetVisible(localStateChatWidgetVisible === 'true')

    }, [localStateChatWidgetVisible])

    // Keep Drift widget visibility in sync with the toggle state on authenticated pages.
    // Handles cases where Drift was initialized elsewhere and loads late.
    useEffect(() => {
        if (!isAuthenticated) return

        const ensureHidden = () => {
            try { window.drift && window.drift.hide && window.drift.hide() } catch (e) { /* noop */ }
        }
        const ensureShownAndIdentified = () => {
            try {
                if (window.drift) {
                    window.drift.show && window.drift.show()
                    if (currUser && currUser.id) {
                        window.drift.identify && window.drift.identify(currUser.id, { email: currUser.email, company: currUser.customerId })
                    }
                }
            } catch (e) { /* noop */ }
        }

        if (!chatWidgetVisible) {
            // Hide immediately if possible
            ensureHidden()

            // Also hide on Drift ready if it initializes late
            if (typeof window !== 'undefined') {
                if (window.drift && window.drift.on) {
                    window.drift.on('ready', ensureHidden)
                } else {
                    // Poll briefly for Drift to appear, then hide
                    const intervalId = setInterval(() => {
                        if (window.drift) {
                            ensureHidden()
                            window.drift.on && window.drift.on('ready', ensureHidden)
                            clearInterval(intervalId)
                        }
                    }, 200)
                    const timeoutId = setTimeout(() => clearInterval(intervalId), 3000)
                    return () => { clearInterval(intervalId); clearTimeout(timeoutId) }
                }
            }
        } else {
            // Show and identify if possible, including late init
            if (typeof window !== 'undefined') {
                if (window.drift) {
                    ensureShownAndIdentified()
                } else {
                    const intervalId = setInterval(() => {
                        if (window.drift) {
                            ensureShownAndIdentified()
                            clearInterval(intervalId)
                        }
                    }, 200)
                    const timeoutId = setTimeout(() => clearInterval(intervalId), 3000)
                    return () => { clearInterval(intervalId); clearTimeout(timeoutId) }
                }
            }
        }
    }, [chatWidgetVisible, isAuthenticated, currUser])

    const handleChatWidgetVisible = (e) => {
        e.preventDefault();

        if (!chatWidgetVisible) {
            setChatWidgetVisible(true)
            window.drift && window.drift.show()
            chatWidgetVisible !== undefined && localStorage.setItem("chatWidgetVisibleLocal", 'true')
        } else {
            setChatWidgetVisible(false)
            window.drift && window.drift.hide()
            chatWidgetVisible !== undefined && localStorage.setItem("chatWidgetVisibleLocal", 'false')
        }
    }

    const switchableAccounts = availableAccounts.filter((account) => account.accountType !== 'rollup');
    const showAccountSwitcher = isAuthenticated && switchableAccounts.length > 1;
    const activeAccountId = activeAccount?.customerId || currUser?.customerId || "";
    const activeAccountLabel = (() => {
        const selected = switchableAccounts.find((account) => account.customerId === activeAccountId);
        return selected?.companyName || selected?.customerId || activeAccountId;
    })();

    const [accountMenuAnchorEl, setAccountMenuAnchorEl] = useState(null);
    const accountMenuOpen = Boolean(accountMenuAnchorEl);
    const handleAccountSwitcherOpen = (event) => {
        const target = event.currentTarget;
        setAccountMenuAnchorEl(target);
    };
    const handleAccountSwitcherClose = () => setAccountMenuAnchorEl(null);
    const handleAccountChange = (customerId) => {
        handleAccountSwitch && handleAccountSwitch(customerId);
        setAccountMenuAnchorEl(null);
    };


    return (
        <div
        // className={classes.root}
        >
            {/*<CssBaseline/>*/}
            {/*<Alert>test</Alert>*/}
            <ScrollToStyleAppBar>
                <AppBar
                    open={props.open}
                    top={props.announcementToolbarHeight}
                    isAuthenticated={isAuthenticated}
                    showQPilotChat={props.showQPilotChat}
                    isMobile={props.isMobile}
                    drawerWidth={props.drawerWidth}
                    sx={{
                        background: isAuthenticated ? "rgb(249, 249, 249)" : "#FFF",
                        borderBottom: isAuthenticated ? "1px solid rgba(0, 0, 0, 0.12)" : "none",
                    }}
                    elevation={0}
                >

                    <Toolbar
                    variant={isAuthenticated ? "dense" : undefined}
                    sx={isAuthenticated ? { minHeight: 42, maxHeight: 42, height: 42 } : {}}
                    >
                        <Grid container alignItems="center" justifyContent="space-between">
                            {/* Left: Logo or Mobile Menu Icon */}
                            <Grid item xs="auto" sx={{ display: 'flex', alignItems: 'center' }}>
                                {/* ←---------- INSERT the chevron button here */}
                                {isAuthenticated && !(props.isMobile && props.isQpilotFocus) && (
                                    <Box sx={{ pt: 0, display: 'flex', alignItems: 'center', height: 42 }}>        
                                        <IconButton
                                            color="primary"
                                            onClick={props.handleDrawerToggle}
                                            aria-label={props.open ? 'Close navigation' : 'Open navigation'}
                                            edge="start"
                                            disableRipple
                                            sx={{ marginLeft: -2.5 }}
                                            size="large"
                                        >
                                            {props.open
                                                ? null
                                                : <ChevronRightIcon color="primary" />}
                                        </IconButton>
                                    </Box>
                                )}
                                {!isAuthenticated && (
                                    <>
                                        {/* Mobile menu icon for unauthenticated users on mobile */}
                                        <Hidden mdUp>
                                            <IconButton
                                                disableRipple
                                                color="secondary"
                                                aria-label="open drawer"
                                                onClick={props.handleMobileMarketingDrawerToggle}
                                                edge="start"
                                                size="large"
                                            >
                                                {props.mobileMarketingMenuOpen ?
                                                    <ChevronLeftIcon color={"primary"} /> :
                                                    <ChevronRightIcon color={"primary"} />}
                                            </IconButton>
                                            <UnauthenticatedDrawer
                                                variant="permanent"
                                                open={props.mobileMarketingMenuOpen}
                                                drawerWidth={props.drawerWidth}
                                                isAuthenticated={isAuthenticated}
                                            >
                                                <DrawerHeader isAuthenticated={isAuthenticated}>
                                                    <Box display="flex" justifyContent="center" alignItems="center" >
                                                    <IconButton onClick={props.handleMobileMarketingDrawerToggle} size="large" disableRipple>
                                                        {theme.direction === 'rtl' ? <ChevronRightIcon color={"primary"} /> :
                                                            <ChevronLeftIcon color={"primary"} />}
                                                    </IconButton>
                                                    </Box>
                                                </DrawerHeader>
                                                <Divider />
                                                <List style={{ padding: '0px 0px 0px 0px' }}>
                                                    <MobileMarketingMenu handleMarketingMenuClose={handleMarketingMenuClose} />
                                                </List>
                                            </UnauthenticatedDrawer>
                                        </Hidden>
                                    </>
                                )}
                                {/* Logo */}
                                {!isAuthenticated && (
                                    <Link to={"/"} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                        <span style={{ marginRight: "10px", color: "#272931" }}>
                                            <QFlowLogoSVG width='7em' color={"#272931"} />
                                        </span>
                                        <span style={{ color: "#272931", marginTop: '-0.5em' }}>&nbsp;&trade;</span>
                                    </Link>
                                )}
                            </Grid>
                            {/* Center: Menu */}
                            <Grid item xs>
                                <Box display="flex" justifyContent="center" alignItems="center">
                                    {!isAuthenticated && (
                                        <>
                                            <Hidden mdDown>
                                                <ProductMarketingMenuv3 />
                                                <Button disableRipple edge="start" aria-controls="blog" href="https://blog.qflow.ai" sx={{ ...marketingNavButtonSx, ml: 3 }}>
                                                    Blog
                                                </Button>
                                                <Button disableRipple edge="start" component={Link} aria-controls="pricing" to="/pricing" sx={{ ...marketingNavButtonSx, ml: 3 }}>
                                                    Pricing
                                                </Button>
                                                <Button disableRipple edge="start" component={Link} aria-controls="about" to="/about" sx={{ ...marketingNavButtonSx, ml: 1.5 }}>
                                                    About
                                                </Button>
                                            </Hidden>
                                        </>
                                    )}

                                    {/* Add authenticated menu here if needed */}
                                </Box>
                            </Grid>
                            {/* Right: Buttons */}
                            <Grid item xs="auto" sm={isAuthenticated ? 6 : "auto"} sx={{minWidth: 0, maxWidth: "calc(100% - 48px)"}}>
                                {!isAuthenticated && (
                                    <ButtonGroup sx={{ mt: 0.4 }}>
                                        <Button variant="contained" color={"primary"} component={Link} to="/signup">
                                            <b>Get Started</b>
                                        </Button>
                                        <Button style={{ minWidth: "80px" }} sx={{ whiteSpace: "nowrap", flexShrink: 0 }} color={"primary"} component={Link} to="/login" edge="start" variant={"outlined"}>
                                            Log in
                                        </Button>
                                    </ButtonGroup>
                                )}
                                {isAuthenticated && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                                        {showAccountSwitcher && (
                                            <>
                                                <ButtonBase
                                                    onClick={handleAccountSwitcherOpen}
                                                    aria-label={`Switch account, current company: ${activeAccountLabel}`}
                                                    title={activeAccountLabel}
                                                    aria-haspopup="menu"
                                                    aria-expanded={accountMenuOpen ? 'true' : 'false'}
                                                    focusRipple={false}
                                                    sx={{
                                                        display: 'inline-flex',
                                                        minWidth: 0,
                                                        flexShrink: 0,
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        height: {xs: 36, sm: 28},
                                                        maxWidth: {xs: 180, sm: 240},
                                                        pl: 1.25,
                                                        pr: 0.75,
                                                        fontFamily: 'Assistant, sans-serif',
                                                        fontSize: '0.8125rem',
                                                        fontWeight: 500,
                                                        lineHeight: 1,
                                                        color: 'text.primary',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid rgba(0, 0, 0, 0.12)',
                                                        borderRadius: '7px',
                                                        transition: 'border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease',
                                                        '&:hover': {
                                                            borderColor: 'rgba(0, 0, 0, 0.24)',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                        },
                                                        '&:focus-visible': {
                                                            borderColor: 'secondary.main',
                                                            boxShadow: (t) => `0 0 0 1px ${t.palette.secondary.main}`,
                                                        },
                                                        ...(accountMenuOpen && {
                                                            borderColor: 'rgba(0, 0, 0, 0.24)',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                        }),
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: 180,
                                                        }}
                                                    >
                                                        {activeAccountLabel}
                                                    </Box>
                                                    <KeyboardArrowDownIcon
                                                        sx={{
                                                            fontSize: 16,
                                                            color: 'text.secondary',
                                                            transition: 'transform 150ms ease',
                                                            transform: accountMenuOpen ? 'rotate(180deg)' : 'none',
                                                        }}
                                                    />
                                                </ButtonBase>
                                                <Menu
                                                    anchorEl={accountMenuAnchorEl}
                                                    open={accountMenuOpen}
                                                    onClose={handleAccountSwitcherClose}
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                                    MenuListProps={{
                                                        'aria-label': 'Switch account',
                                                        dense: true,
                                                        sx: { py: 0.5 },
                                                    }}
                                                    slotProps={{
                                                        paper: {
                                                            elevation: 0,
                                                            sx: {
                                                                mt: 0.75,
                                                                minWidth: 220,
                                                                maxWidth: 'min(320px, calc(100vw - 32px))',
                                                                maxHeight: 'min(320px, calc(100vh - 96px))',
                                                                border: '1px solid rgba(0, 0, 0, 0.12)',
                                                                borderRadius: '8px',
                                                                boxShadow: '0 4px 16px rgba(15, 18, 30, 0.06)',
                                                                overflowY: 'auto',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {switchableAccounts.map((account) => {
                                                        const isActive = account.customerId === activeAccountId;
                                                        return (
                                                            <MenuItem
                                                                key={account.customerId}
                                                                selected={isActive}
                                                                onClick={() => handleAccountChange(account.customerId)}
                                                                sx={{
                                                                    fontFamily: 'Assistant, sans-serif',
                                                                    fontSize: '0.8125rem',
                                                                    fontWeight: isActive ? 600 : 500,
                                                                    color: 'text.primary',
                                                                    py: 0.75,
                                                                    px: 1.25,
                                                                    mx: 0.5,
                                                                    my: 0.125,
                                                                    borderRadius: '6px',
                                                                    minHeight: 'auto',
                                                                    transition: 'background-color 120ms ease',
                                                                    '&:hover': {
                                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: 'rgba(77, 166, 133, 0.08)',
                                                                        '&:hover': {
                                                                            backgroundColor: 'rgba(77, 166, 133, 0.12)',
                                                                        },
                                                                    },
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={account.companyName || account.customerId}
                                                                    primaryTypographyProps={{
                                                                        fontFamily: 'Assistant, sans-serif',
                                                                        fontSize: '0.8125rem',
                                                                        fontWeight: 'inherit',
                                                                        noWrap: true,
                                                                    }}
                                                                />
                                                                <ListItemIcon
                                                                    sx={{
                                                                        minWidth: 'auto !important',
                                                                        ml: 1.25,
                                                                        color: 'secondary.main',
                                                                        visibility: isActive ? 'visible' : 'hidden',
                                                                    }}
                                                                >
                                                                    <CheckIcon sx={{ fontSize: 16 }} />
                                                                </ListItemIcon>
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Menu>
                                            </>
                                        )}
                                        <TopRightMenu
                                            stripeCustomerId={stripeCustomerId}
                                            handleChatWidgetVisible={handleChatWidgetVisible}
                                            chatWidgetVisible={chatWidgetVisible}
                                            stripePromise={props.stripePromise}
                                            handleClickedLogout={props.handleClickedLogout}
                                        />
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    </Toolbar>
                </AppBar>
            </ScrollToStyleAppBar>

            {
                isAuthenticated &&
                <Hidden mdUp
                >

                    <AuthenticatedDrawer
                        variant="permanent"
                        open={props.open}
                        showQPilotChat={props.showQPilotChat}
                        isMobile={props.isMobile}
                        drawerWidth={props.drawerWidth}
                        isAuthenticated={isAuthenticated}
                    >
                        <DrawerHeader isAuthenticated={isAuthenticated}>
                            <IconButton aria-label={props.open ? 'Close navigation' : 'Open navigation'} onClick={props.handleDrawerToggle} size="large" disableRipple>

                                {props.open ? <ChevronLeftIcon color='primary' /> : <ChevronRightIcon color='primary' />}
                            </IconButton>

                        </DrawerHeader>
                        <Divider />
                        <List>
                            <QNavigationWrapper
                                pathName={props.pathName}
                                drawerWidth={props.drawerWidth}
                                drawerOpen={props.open}
                                onCloseNavigation={props.onCloseNavigation}
                                handleShowQPilotChatChange={props.handleShowQPilotChatChange}
                                showQPilotChat={props.showQPilotChat}
                                isMobile={props.isMobile}
                            />
                        </List>
                    </AuthenticatedDrawer>
                </Hidden>
            }


            {
                isAuthenticated &&
                <Hidden mdDown
                // implementation="css"
                >
                    <AuthenticatedDrawer
                        variant="permanent"
                        open={props.open}
                        showQPilotChat={props.showQPilotChat}
                        isMobile={props.isMobile}
                        drawerWidth={props.drawerWidth}
                        isAuthenticated={isAuthenticated}
                    >
                        <Box sx={{
                            zIndex: 1203,
                            minHeight: isAuthenticated ? 42 : 56, // 56px mobile toolbar height
                            maxHeight: isAuthenticated ? 42 : 56,
                            [theme.breakpoints.up('sm')]: {
                                minHeight: isAuthenticated ? 42 : 64, // 64px desktop toolbar height
                                maxHeight: isAuthenticated ? 42 : 64,
                            },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end'
                        }}>
                            <Box display="flex" justifyContent="center" alignItems="center">
                                <IconButton aria-label={props.open ? 'Close navigation' : 'Open navigation'} onClick={props.handleDrawerToggle} size="large" sx={{ zIndex: 12003, marginLeft: "-2em" }} disableRipple>
                                    {props.open ? <ChevronLeftIcon color="primary" /> : <ChevronRightIcon color="primary" />}
                                </IconButton>
                            </Box>
                        </Box>
                        <Divider />
                        <List>
                            <QNavigationWrapper
                                pathName={props.pathName}
                                drawerWidth={props.drawerWidth}
                                drawerOpen={props.open}
                                onCloseNavigation={props.onCloseNavigation}
                                handleShowQPilotChatChange={props.handleShowQPilotChatChange}
                                showQPilotChat={props.showQPilotChat}
                                isMobile={props.isMobile}
                            /></List>
                    </AuthenticatedDrawer>
                </Hidden>
            }

        </div>
    );
}
